import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'

/**
 * @author rsb
 *
 * The toasts' service manages a collection of toasts.
 * It also provides a public method to push a new toast
 * to the same collection, remove it and clear toasts.
 */
export enum ToastType {
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
  DANGER = 'DANGER',
  INFO = 'INFO'
}

export interface ToastInfo {
  header: string
  body: string
  type: ToastType
  icon: string
  dismissible?: boolean
  /**
   * Short error code shown as a chip in the toast header,
   * e.g. "SITE_NOT_FOUND". Only used by error toasts created
   * via `showError`. Optional everywhere else.
   */
  code?: string
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  public toasts: ToastInfo[] = []

  constructor (private translateService: TranslateService) {}

  public show (
    header: string,
    body: string,
    type: ToastType,
    dismissible?: boolean
  ) {
    let icon = ''
    switch (type) {
      case ToastType.INFO:
        icon = 'fa fa-info-circle'
        break
      case ToastType.SUCCESS:
        icon = 'fa fa-check-circle'
        break
      case ToastType.WARNING:
        icon = 'fa fa-exclamation-circle'
        break
      case ToastType.DANGER:
        icon = 'fa fa-times-circle'
        break
      default:
        icon = 'fa fa-info-circle'
        break
    }
    this.toasts.push({
      header,
      body,
      dismissible,
      type,
      icon
    })
  }

  /**
   * Standardized error toast. Extracts the i18n key from the
   * various shapes the app uses for errors, looks it up in the
   * existing `ERROR.*` catalog, and surfaces the short code as
   * a chip in the toast header so users can quote it to support.
   *
   * @param error      anything thrown/emitted on an error path
   *                   (HttpErrorResponse, Error, { message: '...' })
   * @param fallbackKey i18n key used when no recognizable error
   *                   key can be extracted from `error`. Defaults
   *                   to the generic system error message. Pages
   *                   can pass a page-specific fallback (e.g.
   *                   `TOAST.SERVER_ERROR_EXPORT`) when they want
   *                   to keep an existing message for known cases.
   */
  public showError (error: unknown, fallbackKey = 'ERROR.SYSTEM_ERROR'): void {
    const extracted = this.extractErrorKey(error)
    const key = extracted ?? fallbackKey

    // Resolve the body text. If translate returns the key
    // unchanged (no entry in the catalog) we fall back to the
    // generic system error so the user never sees a raw key.
    let body = this.translateService.instant(key)
    if (body === key) {
      body = this.translateService.instant('ERROR.SYSTEM_ERROR')
    }

    // Only show the code chip when the key actually comes from
    // the ERROR.* catalog. Custom fallback keys (e.g. from
    // TOAST.*) are kept as plain bodies, no chip.
    const code = key.startsWith('ERROR.')
      ? key.slice('ERROR.'.length)
      : undefined

    this.toasts.push({
      header: this.translateService.instant('TOAST.ERROR'),
      body,
      type: ToastType.DANGER,
      icon: 'fa fa-times-circle',
      dismissible: true,
      code
    })
  }

  public remove (toast: ToastInfo) {
    this.toasts = this.toasts.filter((t) => t !== toast)
  }

  public clear () {
    this.toasts.splice(0, this.toasts.length)
  }

  /**
   * Extracts an `ERROR.*` i18n key from whatever the caller hands
   * us. The app emits errors in three shapes:
   *  1. Service layer rethrows business errors as
   *     `throwError(() => new Error(message))` where `message`
   *     is already an `ERROR.*` key.
   *  2. HttpErrorResponse from a real HTTP failure — the actual
   *     i18n key sits at `error.error.message`. (Angular's own
   *     `error.message` is the noisy "Http failure response..."
   *     string we want to avoid.)
   *  3. Plain `{ message: 'ERROR.X' }` objects from non-HTTP
   *     code paths.
   */
  private extractErrorKey (error: unknown): string | null {
    if (!error) {
      return null
    }
    if (error instanceof Error && error.message?.startsWith('ERROR.')) {
      return error.message
    }
    const httpBodyMessage = (error as any)?.error?.message
    if (
      typeof httpBodyMessage === 'string' &&
      httpBodyMessage.startsWith('ERROR.')
    ) {
      return httpBodyMessage
    }
    const directMessage = (error as any)?.message
    if (
      typeof directMessage === 'string' &&
      directMessage.startsWith('ERROR.')
    ) {
      return directMessage
    }
    return null
  }
}
